import { format } from "date-fns";
import {
  Home,
  Pill,
  Shield,
  Stethoscope,
  Syringe,
  UserRound,
} from "lucide-react";

import type { MultiStepFormData } from "@/stores";
import { capitalizeFirstLetter, formatDate, YES_NO_OPTIONS } from "@/utils";
import { buildAddressCardData } from "./buildAddressCardData";

export const getCards = ({
  generalInformationFormData,
  medicalInformationFormData,
  insuranceInformationFormData,
  addressInformationFormData,
  productInformationFormData,
  doctorInformationFormData,
}: MultiStepFormData) => {
  const hasSameShippingAddress =
    addressInformationFormData?.hasSameShippingAddress === YES_NO_OPTIONS.YES;

  const addressData = buildAddressCardData(
    addressInformationFormData,
    hasSameShippingAddress,
  );

  const hasSensorsOnly = productInformationFormData?.product === "sensorsOnly";
  return [
    {
      title: "General information",
      icon: <UserRound className="text-primary-600" size={16} />,
      data: [
        { label: "First name", value: generalInformationFormData?.firstName },
        { label: "Last name", value: generalInformationFormData?.lastName },
        {
          label: "Date of birth",
          value: generalInformationFormData?.dateOfBirth
            ? formatDate(generalInformationFormData.dateOfBirth)
            : "N/A",
        },
        { label: "Email", value: generalInformationFormData?.email },
        {
          label: "Phone",
          value: generalInformationFormData?.phoneNumber,
        },
      ],
      isCompleted: !!generalInformationFormData,
    },
    {
      title: "Medical information",
      icon: <Pill className="text-primary-600" size={16} />,
      data: [
        {
          label: "Insulin",
          value: capitalizeFirstLetter(
            medicalInformationFormData?.isTakingInsulin,
          ),
        },
        {
          label: "Insulin name",
          value: medicalInformationFormData?.insulinNames,
        },
        {
          label: "Insulin Intake",
          value: medicalInformationFormData?.dailyInsulinDose,
        },
        {
          label: "Low blood sugar",
          value: capitalizeFirstLetter(
            medicalInformationFormData?.hasLowBloodSugar,
          ),
        },
      ],
      isCompleted: !!medicalInformationFormData,
    },
    {
      title: "Insurance information",
      icon: <Shield className="text-primary-600" size={16} />,
      data: [
        {
          label: "Primary",
          value: `${insuranceInformationFormData?.primaryPayer} ${insuranceInformationFormData?.primaryPayerMbi}`,
        },
        {
          label: "Secondary",
          value: `${insuranceInformationFormData?.secondaryPayer} ${insuranceInformationFormData?.secondaryPayerId}`,
        },
        {
          label: "Policy name",
          value: insuranceInformationFormData?.supplementaryPolicyName,
        },
      ],
      isCompleted: !!insuranceInformationFormData,
    },
    {
      title: "Address information",
      icon: <Home className="text-primary-600" size={16} />,
      data: addressData,
      isCompleted: !!addressInformationFormData,
    },
    {
      title: "Product information",
      icon: <Syringe className="text-primary-600" size={16} />,
      data: [
        {
          label: "Delivered to",
          value:
            productInformationFormData?.sensorsDeliveryMethod == "sentToHome"
              ? "Home"
              : "Pharmacy",
        },
        {
          label: "CGM",
          value:
            hasSensorsOnly && "cgmReader" in productInformationFormData
              ? productInformationFormData?.cgmReader
              : "Yes",
        },
        {
          label: "Using since",
          value:
            hasSensorsOnly &&
            "year" in productInformationFormData &&
            "month" in productInformationFormData &&
            format(
              new Date(
                productInformationFormData?.year,
                productInformationFormData?.month - 1,
                1,
              ),
              "MMMM yyyy",
            ),
        },
        {
          label: "Last sensors received",
          value:
            hasSensorsOnly &&
            "lastSensorsReceivedYear" in productInformationFormData &&
            "lastSensorsReceivedMonth" in productInformationFormData &&
            format(
              new Date(
                productInformationFormData?.lastSensorsReceivedYear,
                productInformationFormData?.lastSensorsReceivedMonth - 1,
                1,
              ),
              "MMMM yyyy",
            ),
        },
        {
          label: "Sensors received",
          value:
            hasSensorsOnly &&
            "lastSensorsReceivedQty" in productInformationFormData &&
            productInformationFormData?.lastSensorsReceivedQty,
        },
      ],
      isCompleted: !!productInformationFormData,
    },
    {
      title: "Doctor information",
      icon: <Stethoscope className="text-primary-600" size={16} />,
      data: [
        {
          label: "Doctor Name",
          value:
            `${doctorInformationFormData?.doctor?.firstName ?? ""} ${
              doctorInformationFormData?.doctor?.lastName ?? ""
            }`.trim() ?? "N/A",
        },
        {
          label: "Specialty",
          value: doctorInformationFormData?.doctor?.specialty ?? "N/A",
        },
        {
          label: "NPI",
          value: doctorInformationFormData?.doctor?.npi ?? "N/A",
        },
        {
          label: "Phone Number",
          value: doctorInformationFormData?.doctor?.phoneNumber ?? "N/A",
        },
        {
          label: "Fax Number",
          value: doctorInformationFormData?.doctor?.faxNumber ?? "N/A",
        },
        {
          label: "Street Address 1",
          value: doctorInformationFormData?.doctor?.addressLineOne ?? "N/A",
        },
        {
          label: "Street Address 2",
          value: doctorInformationFormData?.doctor?.addressLineTwo ?? "N/A",
        },
        {
          label: "City",
          value: doctorInformationFormData?.doctor?.city ?? "N/A",
        },
        {
          label: "State",
          value: doctorInformationFormData?.doctor?.state ?? "N/A",
        },
        {
          label: "ZIP Code",
          value: doctorInformationFormData?.doctor?.zipCode ?? "N/A",
        },
      ],
      isCompleted: !!doctorInformationFormData,
    },
  ];
};
